import { BillingAttachmentsModal } from '@/components/billing/billing-attachments'
import BillingPaymentShare from '@/components/billing/billing-payment-share'
import ConfirmModal from '@/components/confirm-modal'
import {
    BillingAttachment,
    BillingPayerType,
    BillingPaymentMethod,
    BillingStatus,
} from '@/lib/api/common/interfaces/billing.interface'
import { syncBillingWithProvider } from '@/lib/api/financial/financial-billing-api'
import RequestRefundModal, {
    RefundSubmitData,
} from '@/components/billing/request-refund-modal'
import { submitBillingRefund } from '@/lib/api/financial/billing-refund-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useOfflineSettlementPolicy } from '@/lib/hooks/use-offline-settlement-policy'
import { useInstantRefundPolicy } from '@/lib/hooks/use-instant-refund-policy'
import { useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdAttachFile,
    MdAttachMoney,
    MdCalendarToday,
    MdCheckCircle,
    MdHealthAndSafety,
    MdHourglassTop,
    MdOpenInNew,
    MdPayment,
    MdReceipt,
    MdReplay,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

const STATUS_LABELS: Record<BillingStatus, string> = {
    PENDING: 'Pendente',
    PAID: 'Pago',
    OVERDUE: 'Vencido',
    CANCELLED: 'Cancelado',
    REFUNDED: 'Estornado',
    PARTIALLY_REFUNDED: 'Parcialmente Estornado',
    PARTIALLY_PAID: 'Parcialmente Pago',
    IN_DISPUTE: 'Em Disputa',
    WRITTEN_OFF: 'Baixado',
}

const STATUS_BADGE_CLASS: Record<BillingStatus, string> = {
    PENDING: 'badge-warning',
    PAID: 'badge-success text-white',
    OVERDUE: 'badge-error text-white',
    CANCELLED: 'badge-ghost',
    REFUNDED: 'badge-info text-white',
    PARTIALLY_REFUNDED: 'badge-info',
    PARTIALLY_PAID: 'badge-warning',
    IN_DISPUTE: 'badge-error text-white',
    WRITTEN_OFF: 'badge-ghost',
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CREDIT_CARD: 'Cartão de Crédito',
    DEBIT_CARD: 'Cartão de Débito',
    CASH: 'Dinheiro',
    BANK_TRANSFER: 'Transferência Bancária',
    CHECK: 'Cheque',
    TERMINAL: 'Maquininha (terminal)',
    OTHER: 'Outro',
}

function formatCurrency(cents: number): string {
    return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

export type BillingApprovalStatus =
    | 'NOT_REQUIRED'
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'REJECTED'

export interface BillingInfoData {
    id: number
    status: BillingStatus
    approvalStatus?: BillingApprovalStatus | null
    amount: number
    /**
     * Pagador da cobrança. `HEALTH_PLAN` = coberta por convênio: o `amount` vale
     * zero de propósito (quem cobra o convênio é a fatura do período), e é
     * `invoicedAmount` que diz quanto o atendimento vale.
     */
    payerType?: BillingPayerType | null
    invoicedAmount?: number | null
    healthPlan?: { id: number; name: string } | null
    paidAmount?: number | null
    // Estorno: acumulado devolvido, quando devolvido, e por onde (só quando o
    // dinheiro voltou por fora da conta digital).
    refundedAmount?: number | null
    refundedAt?: string | null
    refundedVia?: string | null
    /**
     * Rateio por atendimento. Presente com mais de um item quando a cobrança cobre
     * a visita inteira do paciente. Ver docs/billing-payout-share.md.
     */
    payoutShares?: Array<{
        sourceType: string
        sourceId: number
        amount: number
    }> | null
    /**
     * Id da cobrança no gateway. Presente = o dinheiro entra por lá, e a baixa pode ser
     * confirmada consultando o provedor em vez de depender do webhook.
     */
    providerPaymentId?: string | null
    paymentMethod?: BillingPaymentMethod | null
    /**
     * Parcelas no cartão combinadas com o pagador. Sem exibir, o operador não tem como
     * conferir o que foi acordado — e é justamente o que o paciente liga para mudar.
     */
    installmentCount?: number | null
    dueDate: string
    paidAt?: string | null
    publicToken?: string | null
    description?: string | null
    pixQrCodeBase64?: string | null
    pixCopyPaste?: string | null
    bankSlipUrl?: string | null
    barcode?: string | null
    digitableLine?: string | null
    paymentLink?: string | null
    attachments?: BillingAttachment[] | null
}

interface BillingInfoSectionProps {
    billing: BillingInfoData | null
    onMarkAsPaid: () => Promise<void>
    /**
     * O cancelamento saiu daqui — agora é feito na tela de detalhes da cobrança.
     * Mantido opcional/ignorado só para não quebrar os callers existentes que ainda passam.
     */
    onCancel?: () => Promise<void>
    canUpdate?: boolean
    /** Recarrega a entidade dona da cobrança após anexar/remover comprovante. */
    onBillingChanged?: () => void | Promise<void>
    /** Título do cabeçalho. Distingue a principal dos complementos quando há vários. */
    title?: string
    /**
     * Esconde o botão "Detalhes". Usado quando o card JÁ está na tela de detalhes
     * daquela cobrança — o link levaria para a própria página.
     */
    hideDetailsLink?: boolean
    /**
     * Esconde o QR Code PIX e o link de pagamento. Usado na tela de detalhes da
     * cobrança, que já mostra os dois em cards próprios.
     */
    hidePaymentShare?: boolean
    /**
     * Qual atendimento está sendo visto. Numa cobrança que cobre vários, o card
     * mostra o total (o que o paciente paga) — e, com esta informação, também
     * quanto daquele total é DESTE atendimento. Sem isso o operador vê R$ 900 na
     * página de um exame de R$ 300 e conclui que o valor está errado.
     */
    appointmentSource?: { sourceType: string; sourceId: number } | null
}

export default function BillingInfoSection({
    billing,
    onMarkAsPaid,
    canUpdate = false,
    onBillingChanged,
    title = 'Cobrança',
    hideDetailsLink = false,
    hidePaymentShare = false,
    appointmentSource,
}: BillingInfoSectionProps) {
    const navigate = useNavigate()

    /**
     * Atendimento coberto por convênio.
     *
     * A cobrança nasce quitada com `amount` zero — quem cobra o convênio é a fatura
     * do período. Exibir os números crus aqui mostraria "Valor R$ 0,00" e "Valor
     * Pago R$ 0,00" num atendimento que vale R$ 200 e não foi pago por ninguém.
     */
    const isHealthPlanCovered = billing?.payerType === 'HEALTH_PLAN'
    const healthPlanLabel = billing?.healthPlan?.name || 'convênio'

    // Cobrança agrupada: um pagamento para vários atendimentos.
    const shares = billing?.payoutShares ?? []
    const isShared = shares.length > 1
    const shareAmount = appointmentSource
        ? (shares.find(
              s =>
                  s.sourceType === appointmentSource.sourceType &&
                  s.sourceId === appointmentSource.sourceId,
          )?.amount ?? null)
        : null

    /**
     * `canUpdate` chega das telas de atendimento e reflete a permissão do MÓDULO MÉDICO
     * (`medical:exams:update` etc). O estorno, porém, é um endpoint do financeiro e exige
     * `financial:billings:update` — quem agenda não necessariamente pode mexer no dinheiro.
     *
     * Sem esta checagem, a recepção veria o botão e levaria um 403 ao clicar.
     */
    const { canUpdate: canUpdateBilling } = useResourcePermissions(
        'financial',
        'billings'
    )

    const [showPayModal, setShowPayModal] = useState(false)
    const [showAttachmentsModal, setShowAttachmentsModal] = useState(false)
    const [paying, setPaying] = useState(false)
    const [checkingProvider, setCheckingProvider] = useState(false)
    const [showRefundModal, setShowRefundModal] = useState(false)
    const [requestingRefund, setRequestingRefund] = useState(false)

    // Recebido por fora do sistema com baixa direta habilitada: dispensa o
    // comprovante (ver `hasPaymentProof` abaixo). Hook antes do early return.
    const { autoSettle: offlineAutoSettle } = useOfflineSettlementPolicy()
    // Muda o rótulo do botão: "Solicitar Estorno" mente quando a empresa não tem fila.
    const { allowInstantRefund } = useInstantRefundPolicy()

    if (!billing) return null

    /**
     * Cobrança gerada no gateway: antes de qualquer baixa manual, pergunta ao provedor
     * qual é a verdade.
     *
     * O webhook pode se perder (provedor fora do ar, deploy no meio do caminho) — o
     * cliente pagou, o dinheiro está na conta digital, e a cobrança segue PENDENTE aqui.
     * Sem esta consulta, o operador só teria a opção de baixar no escuro, anexando um
     * comprovante para um pagamento que o sistema já podia ter confirmado sozinho.
     *
     * Se o gateway confirma o pagamento, a baixa acontece por lá mesmo (com repasse,
     * nota fiscal e confirmação de agendamento) e o modal de baixa manual nem abre. Se
     * não confirma, o operador é avisado e segue o fluxo normal — pagar por fora do
     * gateway é legítimo (PIX direto na conta, dinheiro), e é para isso que existe o
     * comprovante.
     */
    const handlePayClick = async () => {
        // Sem gateway (dinheiro, maquininha, transferência avulsa): baixa manual de
        // sempre — comprovante obrigatório e, se o usuário não for operador financeiro,
        // fila de aprovação.
        if (!billing.providerPaymentId) {
            setShowPayModal(true)
            return
        }

        // Com gateway, quem decide é o provedor, e SÓ ele. O dinheiro entra (ou não) por
        // lá, então não faz sentido pedir comprovante nem aprovação de operador para um
        // fato que o Asaas confirma sozinho: ou ele diz que está pago — e a baixa sai na
        // hora, com repasse e nota fiscal —, ou não, e o operador apenas fica sabendo.
        // Nunca cai no fluxo manual.
        setCheckingProvider(true)
        try {
            const res = await syncBillingWithProvider(billing.id)

            if (!res.success || !res.result) {
                toast.error(
                    res.error ||
                        'Não foi possível consultar o gateway. Tente novamente.'
                )
                return
            }

            const { outcome, message } = res.result

            if (outcome === 'PAID' || outcome === 'ALREADY_PAID') {
                toast.success(message)
            } else if (outcome === 'REFUNDED' || outcome === 'CANCELLED') {
                toast.warning(message)
            } else {
                // AWAITING_PAYMENT / OVERDUE — o gateway ainda não viu o dinheiro.
                toast.info(message)
            }

            onBillingChanged?.()
        } catch {
            toast.error(
                'Não foi possível consultar o gateway. Tente novamente.'
            )
        } finally {
            setCheckingProvider(false)
        }
    }

    const handleMarkAsPaid = async () => {
        // Guard de duplo clique: o ConfirmModal desabilita pelo `loading`, mas o
        // clique repetido antes do re-render ainda chegava a disparar duas
        // requisições — e duas solicitações de baixa para o mesmo lançamento.
        if (paying) return

        setPaying(true)
        try {
            await onMarkAsPaid()
        } finally {
            setPaying(false)
            setShowPayModal(false)
        }
    }

    /**
     * Estorno pedido direto na cobrança do agendamento — sem precisar cancelar o
     * atendimento. Acontece quando o serviço foi prestado mas o valor precisa voltar
     * (cobrança em duplicidade, valor errado, acordo com o paciente).
     */
    /**
     * O modal já monta o payload do caminho vigente (fila ou estorno imediato) e o
     * helper roteia — daí este handler não repetir a regra da política.
     */
    const handleRequestRefund = async (data: RefundSubmitData) => {
        if (requestingRefund) return

        setRequestingRefund(true)
        try {
            const res = await submitBillingRefund(billing.id, data)
            if (res.success) {
                toast.success(res.message)
                setShowRefundModal(false)
                onBillingChanged?.()
            } else {
                // O backend recusa quando o repasse ao parceiro já foi pago, e
                // no imediato também quando a senha não confere.
                toast.error(res.error || 'Erro ao estornar')
            }
        } catch {
            toast.error('Erro ao estornar')
        } finally {
            setRequestingRefund(false)
        }
    }

    const isPending =
        billing.status === 'PENDING' || billing.status === 'OVERDUE'

    /**
     * Cobrança liquidada pelo gateway (PIX/boleto/cartão): o Asaas dá baixa
     * sozinho. Não oferece baixa manual daqui — mandar para a fila de aprovação
     * com comprovante é trabalho perdido (e, com a charge viva, marcar como paga
     * deixaria o PIX ainda pagável). Com charge emitida resta "Verificar
     * pagamento"; sem charge, o caminho é gerar os dados na tela da cobrança.
     */
    const isGatewayBilling =
        !!billing.paymentMethod &&
        ['PIX', 'BOLETO', 'CREDIT_CARD'].includes(billing.paymentMethod)

    // Há dinheiro do cliente em caixa para devolver. `PARTIALLY_REFUNDED` entra: o saldo
    // que sobrou ainda pode ser estornado num segundo pedido.
    // Convênio fica de fora: a cobrança consta como paga por convenção (nasce
    // quitada com valor zero para liberar o repasse), não porque alguém pagou —
    // não há dinheiro do paciente a devolver. O backend recusa o estorno; sem
    // esconder o botão, o operador levaria o erro só depois de clicar.
    const isRefundable =
        !isHealthPlanCovered &&
        (billing.status === 'PAID' ||
            billing.status === 'PARTIALLY_PAID' ||
            billing.status === 'PARTIALLY_REFUNDED')

    // Ação (pagamento ou cancelamento) enfileirada para aprovação de um operador
    // financeiro. Enquanto pendente, nenhuma nova ação pode ser disparada.
    const isAwaitingApproval = billing.approvalStatus === 'PENDING_APPROVAL'

    /**
     * Recebimento por fora do sistema (dinheiro, maquininha, outro) com a baixa
     * direta habilitada nas Configurações Financeiras: o backend dispensa
     * comprovante e fila, então o botão não pode ficar bloqueado aqui.
     *
     * Normalmente essas cobranças já nascem pagas (ou viram pagas na edição que
     * define o método); isto cobre as que ficaram pendentes de antes da
     * configuração ser ligada.
     */
    const isDirectSettlement =
        offlineAutoSettle &&
        !!billing.paymentMethod &&
        ['CASH', 'TERMINAL', 'OTHER'].includes(billing.paymentMethod)

    // Fora esse caso, a baixa manual exige o comprovante de pagamento anexado à
    // cobrança. O anexo é feito na tela de detalhes da cobrança.
    const hasPaymentProof =
        isDirectSettlement ||
        (billing.attachments || []).some(att => !att.rejected)

    return (
        <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden">
            <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MdReceipt className="text-xl text-secondary" />
                        <h3 className="text-lg font-bold text-base-content">
                            {title}
                        </h3>
                        <span
                            className={`badge ${STATUS_BADGE_CLASS[billing.status]}`}
                        >
                            {STATUS_LABELS[billing.status]}
                        </span>
                        {/* A cobrança não é só deste atendimento: dizer isso no
                            cabeçalho evita o operador achar que o valor está
                            errado, ou pagar/estornar achando que afeta só um. */}
                        {isShared && (
                            <span className="badge badge-outline badge-secondary gap-1">
                                Agrupada · {shares.length} atendimentos
                            </span>
                        )}
                        {isAwaitingApproval && (
                            <span className="badge badge-warning gap-1">
                                <MdHourglassTop className="text-sm" />
                                Aguardando aprovação
                            </span>
                        )}
                    </div>
                    {!hideDetailsLink && (
                        <button
                            onClick={() =>
                                navigate(`/financial/billings/${billing.id}`)
                            }
                            className="btn btn-ghost btn-sm gap-1"
                            title="Ver detalhes completos da cobrança"
                        >
                            <MdOpenInNew className="text-base" />
                            <span className="hidden sm:inline text-xs">
                                Detalhes
                            </span>
                        </button>
                    )}
                </div>
            </div>

            <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 bg-base-200/50 rounded-xl">
                        <div className="flex items-center gap-1 mb-1">
                            <MdAttachMoney className="text-sm text-secondary" />
                            <p className="text-xs text-base-content/60">
                                Valor
                            </p>
                        </div>
                        {/* Coberto por convênio: mostrar o `amount` daria "R$ 0,00",
                            que o operador leria como atendimento sem valor. O valor
                            real é o faturado ao convênio. */}
                        <p className="font-bold text-secondary">
                            {formatCurrency(
                                isHealthPlanCovered
                                    ? (billing.invoicedAmount ?? 0)
                                    : billing.amount
                            )}
                        </p>
                        {isHealthPlanCovered && (
                            <p className="mt-0.5 text-[11px] leading-tight text-base-content/60">
                                Coberto por {healthPlanLabel}
                            </p>
                        )}
                        {isShared && (
                            <p className="mt-0.5 text-[11px] leading-tight text-base-content/50">
                                {shareAmount != null
                                    ? `Deste atendimento: ${formatCurrency(shareAmount)}`
                                    : `Cobre ${shares.length} atendimentos`}
                            </p>
                        )}
                    </div>

                    {/* Convênio: o card de "Valor Pago" não se aplica — nada foi
                        recebido do paciente, e o convênio paga a fatura do período.
                        No lugar dele, quem cobre o atendimento. */}
                    {isHealthPlanCovered ? (
                        <div className="p-3 bg-secondary/10 rounded-xl">
                            <div className="flex items-center gap-1 mb-1">
                                <MdHealthAndSafety className="text-sm text-secondary" />
                                <p className="text-xs text-base-content/60">
                                    Cobertura
                                </p>
                            </div>
                            <p className="font-bold text-secondary truncate">
                                {healthPlanLabel}
                            </p>
                            <p className="mt-0.5 text-[11px] leading-tight text-base-content/60">
                                Faturado ao convênio
                            </p>
                        </div>
                    ) : (
                        billing.paidAmount != null &&
                        billing.paidAmount > 0 && (
                            <div className="p-3 bg-success/10 rounded-xl">
                                <div className="flex items-center gap-1 mb-1">
                                    <MdCheckCircle className="text-sm text-success" />
                                    <p className="text-xs text-base-content/60">
                                        Valor Pago
                                    </p>
                                </div>
                                <p className="font-bold text-success">
                                    {formatCurrency(billing.paidAmount)}
                                </p>
                            </div>
                        )
                    )}

                    <div className="p-3 bg-base-200/50 rounded-xl">
                        <div className="flex items-center gap-1 mb-1">
                            <MdCalendarToday className="text-sm text-base-content/60" />
                            <p className="text-xs text-base-content/60">
                                Vencimento
                            </p>
                        </div>
                        <p className="font-medium">
                            {formatDate(billing.dueDate)}
                        </p>
                    </div>

                    {billing.paymentMethod && (
                        <div className="p-3 bg-base-200/50 rounded-xl">
                            <div className="flex items-center gap-1 mb-1">
                                <MdPayment className="text-sm text-base-content/60" />
                                <p className="text-xs text-base-content/60">
                                    Pagamento
                                </p>
                            </div>
                            <p className="font-medium">
                                {PAYMENT_METHOD_LABELS[billing.paymentMethod] ||
                                    billing.paymentMethod}
                                {(billing.installmentCount ?? 0) > 1 && (
                                    <span className="ml-1 text-base-content/70">
                                        em {billing.installmentCount}x
                                    </span>
                                )}
                            </p>
                        </div>
                    )}

                    {billing.paidAt && (
                        <div className="p-3 bg-success/10 rounded-xl">
                            <div className="flex items-center gap-1 mb-1">
                                <MdCheckCircle className="text-sm text-success" />
                                <p className="text-xs text-base-content/60">
                                    Pago em
                                </p>
                            </div>
                            <p className="font-medium text-success">
                                {formatDate(billing.paidAt)}
                            </p>
                        </div>
                    )}

                    {/* Estorno. Sem mostrar o valor devolvido, uma cobrança de R$ 200 com
                        R$ 50 estornados aparece só como "Parcialmente Estornado" — e
                        ninguém sabe quanto voltou nem quanto ficou. */}
                    {billing.refundedAmount != null &&
                        billing.refundedAmount > 0 && (
                            <div className="p-3 bg-info/10 rounded-xl">
                                <div className="flex items-center gap-1 mb-1">
                                    <MdReplay className="text-sm text-info" />
                                    <p className="text-xs text-base-content/60">
                                        Valor Estornado
                                    </p>
                                </div>
                                <p className="font-bold text-info">
                                    {formatCurrency(billing.refundedAmount)}
                                </p>
                                {billing.refundedAt && (
                                    <p className="text-xs text-base-content/60 mt-1">
                                        em {formatDate(billing.refundedAt)}
                                        {billing.refundedVia
                                            ? ` · ${billing.refundedVia}`
                                            : ''}
                                    </p>
                                )}
                            </div>
                        )}
                </div>

                {!hidePaymentShare && (
                    <BillingPaymentShare
                        billing={billing}
                        // Gerar o QR é ação do financeiro (`generate-payment`), não do
                        // atendimento: mesma permissão do estorno, logo acima.
                        canGenerate={canUpdateBilling && !isAwaitingApproval}
                        onChanged={onBillingChanged}
                    />
                )}

                {canUpdate && isPending && isAwaitingApproval && (
                    <div className="pt-4 border-t border-base-300/50">
                        <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/30 rounded-xl">
                            <MdHourglassTop className="text-2xl text-warning shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold text-base-content">
                                    Aguardando aprovação
                                </p>
                                <p className="text-sm text-base-content/70 mt-1">
                                    A solicitação de aprovação foi enviada e
                                    está aguardando a análise de um operador
                                    financeiro. Enquanto isso, nenhuma nova ação
                                    pode ser realizada nesta cobrança.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {canUpdate && isPending && !isAwaitingApproval && (
                    <div className="flex items-center gap-2 pt-2 border-t border-base-300/50">
                        {/* Cobrança do gateway não é "marcada" como paga por ninguém: o
                            provedor confirma, e o sistema baixa. Por isso o rótulo muda —
                            e nem comprovante nem aprovação são exigidos. Gateway SEM
                            charge emitida não mostra botão: não há o que baixar nem o
                            que verificar (o caminho é gerar os dados na cobrança). */}
                        {(!isGatewayBilling || !!billing.providerPaymentId) && (
                        <button
                            onClick={handlePayClick}
                            disabled={
                                checkingProvider ||
                                (!hasPaymentProof && !billing.providerPaymentId)
                            }
                            title={
                                billing.providerPaymentId
                                    ? 'Consultar o gateway e, se o pagamento tiver entrado, dar baixa'
                                    : isDirectSettlement
                                      ? 'Marcar cobrança como paga (recebida por fora do sistema)'
                                      : hasPaymentProof
                                        ? 'Marcar cobrança como paga'
                                        : 'Anexe o comprovante de pagamento antes de marcar como paga'
                            }
                            className="btn btn-success btn-sm gap-1"
                        >
                            {checkingProvider ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdCheckCircle className="text-base" />
                            )}
                            {checkingProvider
                                ? 'Consultando gateway...'
                                : billing.providerPaymentId
                                  ? 'Verificar pagamento'
                                  : 'Pago'}
                        </button>
                        )}
                        <button
                            onClick={() => setShowAttachmentsModal(true)}
                            className="btn btn-ghost btn-sm gap-1"
                        >
                            <MdAttachFile className="text-base" />
                            Comprovante
                        </button>
                    </div>
                )}

                {/* Cobrança paga: até aqui a única ação possível era cancelar o
                    atendimento inteiro. Mas o serviço pode ter sido prestado e ainda
                    assim ser preciso devolver o dinheiro (cobrança em duplicidade, valor
                    errado, acordo com o paciente) — daí o estorno direto na cobrança. */}
                {canUpdateBilling && isRefundable && !isAwaitingApproval && (
                    <div className="flex items-center gap-2 pt-2 border-t border-base-300/50">
                        <button
                            onClick={() => setShowRefundModal(true)}
                            disabled={requestingRefund}
                            className="btn btn-info btn-outline btn-sm gap-1"
                            title={
                                allowInstantRefund
                                    ? 'Estornar agora, sem passar pela fila de aprovação'
                                    : 'Solicitar estorno ao operador financeiro'
                            }
                        >
                            {requestingRefund ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdReplay className="text-base" />
                            )}
                            {allowInstantRefund ? 'Estornar' : 'Solicitar Estorno'}
                        </button>
                        <button
                            onClick={() => setShowAttachmentsModal(true)}
                            className="btn btn-ghost btn-sm gap-1"
                        >
                            <MdAttachFile className="text-base" />
                            Comprovante
                        </button>
                    </div>
                )}
            </div>

            <RequestRefundModal
                isOpen={showRefundModal}
                onClose={() => setShowRefundModal(false)}
                onConfirm={handleRequestRefund}
                loading={requestingRefund}
                billing={billing}
            />

            <BillingAttachmentsModal
                isOpen={showAttachmentsModal}
                onClose={() => setShowAttachmentsModal(false)}
                billingId={billing.id}
                attachments={billing.attachments}
                onChanged={() => onBillingChanged?.()}
                showProofWarning={isPending && !hasPaymentProof}
            />

            <ConfirmModal
                isOpen={showPayModal}
                onClose={() => setShowPayModal(false)}
                onConfirm={handleMarkAsPaid}
                title="Marcar como pago"
                message={`Deseja marcar esta cobrança de ${formatCurrency(billing.amount)} como paga?`}
                confirmButtonText="Confirmar Pagamento"
                cancelButtonText="Voltar"
                loading={paying}
                confirmButtonClass="btn-success"
            />
        </div>
    )
}
